import { useEffect, useState } from 'react'
import { fn } from '../auth/supabase'
import { formatDate } from '../lib/date'

interface NextFixture {
  team: string
  grade: string
  fixture: {
    date: string
    start_time: string | null
    round_name: string | null
    round_abbreviation: string | null
    home_away: 'home' | 'away'
    opponent_name: string | null
    venue_name: string | null
    court_name: string | null
  } | null
  bye: { round_name: string | null; round_abbreviation: string | null; date: string | null } | null
}

/**
 * What a signed-out visitor sees.
 *
 * Amendment 03.1(3): the next fixture and nothing else. It deliberately does NOT
 * read the snapshot, which carries the roster -- this comes from a separate
 * endpoint that returns a date, an opponent and a venue, so no name can leak to
 * someone who has not signed in.
 */
export function Landing({ onSignIn }: { onSignIn(): void }) {
  const [data, setData] = useState<NextFixture | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    const url = fn('next-fixture')
    if (!url) {
      setFailed(true)
      return
    }
    let cancelled = false
    fetch(url)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((body: NextFixture) => {
        if (!cancelled) setData(body)
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const f = data?.fixture
  const bye = data?.bye

  return (
    <div className="landing">
      <div className="landing__card">
        <div className="landing__brand">
          <span className="app__logo" aria-hidden="true" />
          <div>
            <div className="landing__app">Hustle Stats</div>
            <div className="landing__team">{data?.team ?? 'Firehawks'}</div>
          </div>
        </div>

        {f ? (
          <div className="landing__fixture">
            <div className="landing__label">Next game</div>
            <div className="landing__opponent">
              {f.home_away === 'home' ? 'vs' : '@'} {f.opponent_name ?? 'TBC'}
            </div>
            <div className="landing__when">
              {f.round_abbreviation ? `${f.round_abbreviation} · ` : ''}
              {formatDate(f.date)}
              {f.start_time ? ` · ${f.start_time.slice(0, 5)}` : ''}
            </div>
            {f.venue_name && (
              <div className="landing__venue">
                {f.venue_name}
                {f.court_name ? ` · ${f.court_name}` : ''}
              </div>
            )}
          </div>
        ) : bye ? (
          <div className="landing__fixture">
            <div className="landing__label">{bye.round_abbreviation}</div>
            <div className="landing__opponent">Bye, no game</div>
          </div>
        ) : (
          <div className="landing__fixture">
            <div className="landing__opponent">{failed ? 'Fixture unavailable' : 'Loading fixture...'}</div>
          </div>
        )}

        <button type="button" className="btn btn--primary btn--start" onClick={onSignIn}>
          Sign in
        </button>
        <p className="muted small landing__note">
          Stats, the roster and the live tally are for the team. Sign in to see them.
        </p>
      </div>
    </div>
  )
}
